import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { theme } from '../theme';
import { useAuthStore } from '../stores/platformAuthStore';
import { useProjectStore, type Collaborator, type RestorePoint } from '../stores/projectStore';
import { useConfirmModal } from '../components/ConfirmModal';

const styles = {
  container: {
    maxWidth: '640px',
    margin: '0 auto',
    padding: '32px 20px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    color: theme.textPrimary,
    marginBottom: '24px',
  },
  card: {
    background: theme.bgPanel,
    borderRadius: theme.radiusLg,
    border: `1px solid ${theme.border}`,
    padding: '24px',
    marginBottom: '24px',
  },
  field: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    color: theme.textSecondary,
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: theme.radiusMd,
    border: `1px solid ${theme.border}`,
    background: theme.bgInput,
    color: theme.textPrimary,
    fontSize: '14px',
    outline: 'none',
  },
  btn: {
    padding: '8px 20px',
    borderRadius: theme.radiusMd,
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
    background: theme.accent,
    color: theme.accentText,
  },
  dangerCard: {
    background: theme.errorBg,
    borderRadius: theme.radiusLg,
    border: `1px solid ${theme.danger}`,
    padding: '24px',
  },
  dangerTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: theme.error,
    marginBottom: '12px',
  },
  dangerBtn: {
    padding: '8px 20px',
    borderRadius: theme.radiusMd,
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
    background: theme.danger,
    color: theme.accentText,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: `1px solid ${theme.border}`,
    fontSize: '14px',
    color: theme.textPrimary,
  },
  addRow: {
    display: 'flex',
    gap: '8px',
    marginTop: '16px',
  },
  info: {
    fontSize: '13px',
    color: theme.textMuted,
    marginBottom: '12px',
  },
};

const KIND_LABELS: Record<RestorePoint['kind'], string> = {
  daily: 'Daily',
  'before-restore': 'Before a restore',
  'before-import': 'Before a replace from zip',
  imported: 'Imported',
  named: 'Named version',
};

function sizeLabel(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

interface ProjectDetail {
  id: string;
  ownerId: string;
  slug: string;
  name: string;
  status: string;
  plan: string;
  maxPlayers: number;
  maxMaps: number;
  maxStorageMb: number;
}

export function ProjectSettings() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [newUsername, setNewUsername] = useState('');
  const [confirm, confirmModal] = useConfirmModal();
  const { user, fetchMe } = useAuthStore();
  const {
    fetchCollaborators,
    addCollaborator,
    removeCollaborator,
    fetchRestorePoints,
    saveNamedVersion,
    deleteRestorePoint,
    restoreFromPoint,
    replaceFromZip,
    exportProject,
  } = useProjectStore();
  const [points, setPoints] = useState<RestorePoint[]>([]);
  const [versionName, setVersionName] = useState('');
  const [working, setWorking] = useState('');
  const [safetyNotice, setSafetyNotice] = useState('');

  const token = localStorage.getItem('platform_token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  useEffect(() => {
    fetch(`/api/platform/projects/${id}`, { headers })
      .then((r) => r.json())
      .then((data) => {
        setProject(data);
        setName(data.name);
        setSlug(data.slug);
      })
      .catch(() => setError('Failed to load project'));
  }, [id]);

  const loadCollaborators = () => {
    if (!id) return;
    fetchCollaborators(id)
      .then(setCollaborators)
      .catch((err) =>
        setError(err instanceof Error ? err.message : 'Failed to load collaborators')
      );
  };

  useEffect(loadCollaborators, [id]);

  const loadPoints = () => {
    if (!id) return;
    fetchRestorePoints(id)
      .then(setPoints)
      .catch(() => setPoints([]));
  };

  // Restore points are owner-only; the request is refused for anyone else.
  useEffect(() => {
    if (project && user && user.id === project.ownerId) loadPoints();
  }, [project, user]);

  const runSafety = async (label: string, fn: () => Promise<void>, notice = '') => {
    setError('');
    setSafetyNotice('');
    setWorking(label);
    try {
      await fn();
      if (notice) setSafetyNotice(notice);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    }
    setWorking('');
    loadPoints();
  };

  const handleSaveVersion = () => {
    if (!id || !versionName.trim()) return;
    void runSafety('save', async () => {
      await saveNamedVersion(id, versionName.trim());
      setVersionName('');
    });
  };

  const handleRestore = async (point: RestorePoint) => {
    if (!id) return;
    const ok = await confirm({
      title: 'Restore this version',
      message:
        'The game stops, goes back to this version, and starts again if it was running. Players in the game are disconnected. A restore point of the game as it is now is saved first. Reload any open editor tabs afterwards.',
      confirmLabel: 'Restore',
    });
    if (!ok) return;
    void runSafety(
      `restore:${point.id}`,
      () => restoreFromPoint(id, point.id),
      'Restored. Reload any open editor tabs.'
    );
  };

  const handleDeletePoint = async (point: RestorePoint) => {
    if (!id) return;
    const ok = await confirm({
      title: 'Delete this version',
      message: `Delete "${point.name}"? It cannot be restored afterwards.`,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    void runSafety(`delete:${point.id}`, () => deleteRestorePoint(id, point.id));
  };

  const handleReplace = async (file: File | null) => {
    if (!id || !file) return;
    const ok = await confirm({
      title: 'Replace from a zip',
      message:
        'The game is replaced by the contents of this zip. A restore point of the game as it is now is saved first, so you can undo this. Players in the game are disconnected.',
      confirmLabel: 'Replace',
    });
    if (!ok) return;
    void runSafety(
      'replace',
      () => replaceFromZip(id, file),
      'Replaced. Reload any open editor tabs.'
    );
  };

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const handleAddCollaborator = async () => {
    if (!id || !newUsername.trim()) return;
    setError('');
    try {
      await addCollaborator(id, newUsername.trim());
      setNewUsername('');
      loadCollaborators();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add collaborator');
    }
  };

  const handleRemoveCollaborator = async (userId: string, leaving: boolean) => {
    if (!id) return;
    if (
      leaving &&
      !(await confirm({
        title: 'Leave Project',
        message: 'Leave this project? You will lose access to it.',
        confirmLabel: 'Leave',
      }))
    )
      return;
    setError('');
    try {
      await removeCollaborator(id, userId);
      if (leaving) navigate('/dashboard', { replace: true });
      else loadCollaborators();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove collaborator');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`/api/platform/projects/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ name, slug }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Move to Trash',
      message:
        'The game stops and moves to the trash. You can restore it from your dashboard for 30 days. After that it is deleted for good.',
      confirmLabel: 'Move to trash',
    });
    if (!ok) return;
    try {
      const res = await fetch(`/api/platform/projects/${id}`, {
        method: 'DELETE',
        headers,
      });
      if (res.ok) navigate('/dashboard', { replace: true });
      else
        setError(
          ((await res.json().catch(() => ({}))) as { error?: string }).error || 'Failed to delete'
        );
    } catch {
      setError('Failed to delete');
    }
  };

  if (!project) {
    return (
      <div style={styles.container}>
        <div style={{ color: theme.textMuted }}>{error || 'Loading...'}</div>
      </div>
    );
  }

  const isOwner = !!user && user.id === project.ownerId;

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Project Settings</h1>

      {error && (
        <div style={{ color: theme.error, marginBottom: '12px', fontSize: '13px' }}>{error}</div>
      )}

      {isOwner && (
        <>
          <div style={styles.card}>
            <div style={styles.field}>
              <label style={styles.label}>Project Name</label>
              <input style={styles.input} value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Slug</label>
              <input style={styles.input} value={slug} onChange={(e) => setSlug(e.target.value)} />
            </div>
            <div style={styles.info}>
              Plan: <strong>{project.plan}</strong> | Max Players: {project.maxPlayers} | Max Maps:{' '}
              {project.maxMaps} | Storage: {project.maxStorageMb}MB
            </div>
            <button style={styles.btn} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </>
      )}

      <div style={styles.card}>
        <div style={styles.dangerTitle}>Collaborators</div>
        {collaborators.map((c) => (
          <div key={c.userId} style={styles.row}>
            <span>
              {c.username}{' '}
              <span style={{ color: theme.textMuted }}>({c.pending ? 'invited' : c.role})</span>
            </span>
            {c.role !== 'owner' && isOwner && (
              <button
                style={styles.dangerBtn}
                onClick={() => handleRemoveCollaborator(c.userId, false)}
              >
                {c.pending ? 'Cancel invite' : 'Remove'}
              </button>
            )}
            {c.userId === user?.id && !isOwner && (
              <button
                style={styles.dangerBtn}
                onClick={() => handleRemoveCollaborator(c.userId, true)}
              >
                Leave project
              </button>
            )}
          </div>
        ))}
        {isOwner && (
          <div style={styles.addRow}>
            <input
              style={styles.input}
              placeholder="Username"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddCollaborator()}
            />
            <button style={styles.btn} onClick={handleAddCollaborator}>
              Add
            </button>
          </div>
        )}
      </div>

      {isOwner && (
        <>
          <div style={styles.card}>
            <div style={styles.dangerTitle}>Restore points</div>
            <div style={styles.info}>
              A restore point is saved every day the game changes, and before every restore. Save a
              named version before a big change so you can always get back to it.
            </div>
            {safetyNotice && (
              <div style={{ ...styles.info, color: theme.textPrimary }}>{safetyNotice}</div>
            )}
            {points.length === 0 && <div style={styles.info}>No restore points yet.</div>}
            {points.map((p) => (
              <div key={p.id} style={styles.row}>
                <span>
                  {p.kind === 'named' ? <strong>{p.name}</strong> : KIND_LABELS[p.kind]}{' '}
                  <span style={{ color: theme.textMuted }}>
                    {new Date(p.createdAt).toLocaleString()} - {sizeLabel(p.sizeBytes)}
                  </span>
                </span>
                <span style={{ display: 'flex', gap: '8px' }}>
                  <button
                    style={styles.btn}
                    disabled={working !== ''}
                    onClick={() => handleRestore(p)}
                  >
                    {working === `restore:${p.id}` ? 'Restoring...' : 'Restore'}
                  </button>
                  {p.kind === 'named' && (
                    <button
                      style={styles.dangerBtn}
                      disabled={working !== ''}
                      onClick={() => handleDeletePoint(p)}
                    >
                      Delete
                    </button>
                  )}
                </span>
              </div>
            ))}
            <div style={styles.addRow}>
              <input
                style={styles.input}
                placeholder="Version name, e.g. Before the castle rework"
                maxLength={80}
                value={versionName}
                onChange={(e) => setVersionName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSaveVersion()}
              />
              <button
                style={styles.btn}
                disabled={working !== '' || !versionName.trim()}
                onClick={handleSaveVersion}
              >
                {working === 'save' ? 'Saving...' : 'Save a named version'}
              </button>
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.dangerTitle}>Export</div>
            <div style={styles.info}>
              Download the whole game as a zip: its database (including player accounts), scripts
              and assets. You can import the zip as a new game from your dashboard, or replace this
              game with one.
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                style={styles.btn}
                disabled={working !== ''}
                onClick={() => void runSafety('export', () => exportProject(project.id))}
              >
                {working === 'export' ? 'Preparing...' : 'Download as zip'}
              </button>
              <label style={{ ...styles.btn, display: 'inline-block' }}>
                {working === 'replace' ? 'Replacing...' : 'Replace from a zip'}
                <input
                  type="file"
                  accept=".zip,application/zip"
                  style={{ display: 'none' }}
                  disabled={working !== ''}
                  onChange={(e) => {
                    void handleReplace(e.target.files?.[0] ?? null);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
          </div>

          <div style={styles.dangerCard}>
            <div style={styles.dangerTitle}>Danger Zone</div>
            <div style={styles.info}>Move to trash. You can restore it for 30 days.</div>
            <button style={styles.dangerBtn} onClick={handleDelete}>
              Move to Trash
            </button>
          </div>
        </>
      )}
      {confirmModal}
    </div>
  );
}
